import * as THREE from 'three';
import { Entity } from '../state/store';
import { BIMElement, WallElement } from '../core/types';
import { WallJoinSolver } from '../core/WallJoinSolver';

/**
 * Interface for Wall Data
 */
export interface WallData {
    start: number[];
    end: number[];
    thickness: number;
    height: number;
    center?: number[];
    angle?: number;
    length?: number;
    profile?: number[][]; // Array of [x, y] points (2D)
    joinedAtStart?: boolean;
    joinedAtEnd?: boolean;
}

// Helper for 2D math
type Vec2 = { x: number, y: number };

/**
 * Join/move math reads raw endpoints and thickness (new THREE.Vector3(...start),
 * getWallLines(...)) — a wall committed without a usable geometry payload
 * (e.g. a bare programmatic create) would throw "Cannot read properties of
 * undefined (reading 'start')" and poison every later wall operation, so the
 * solvers skip such elements instead.
 */
function hasUsableWallData(data: unknown): data is Pick<WallData, 'start' | 'end' | 'thickness'> {
    const d = data as WallData | undefined;
    if (!d || !Array.isArray(d.start) || !Array.isArray(d.end)) return false;
    const finiteTriple = (v: unknown[]) => v.length === 3 && v.every((n) => typeof n === 'number' && Number.isFinite(n));
    return finiteTriple(d.start) && finiteTriple(d.end) && typeof d.thickness === 'number' && Number.isFinite(d.thickness);
}

function toVec2(v: THREE.Vector3, plane: 'XY' | 'XZ'): Vec2 {
    return plane === 'XY' ? { x: v.x, y: v.y } : { x: v.x, y: v.z };
}

function dist(v1: Vec2, v2: Vec2): number {
    return Math.sqrt(Math.pow(v2.x - v1.x, 2) + Math.pow(v2.y - v1.y, 2));
}

function sub(v1: Vec2, v2: Vec2): Vec2 {
    return { x: v1.x - v2.x, y: v1.y - v2.y };
}

function add(v1: Vec2, v2: Vec2): Vec2 {
    return { x: v1.x + v2.x, y: v1.y + v2.y };
}

function mul(v: Vec2, s: number): Vec2 {
    return { x: v.x * s, y: v.y * s };
}

function normalize(v: Vec2): Vec2 {
    const len = Math.sqrt(v.x * v.x + v.y * v.y);
    return len === 0 ? { x: 0, y: 0 } : { x: v.x / len, y: v.y / len };
}



// Intersection of two infinite lines
function getLineIntersection(p1: Vec2, p2: Vec2, p3: Vec2, p4: Vec2): Vec2 | null {
    const x1 = p1.x, y1 = p1.y;
    const x2 = p2.x, y2 = p2.y;
    const x3 = p3.x, y3 = p3.y;
    const x4 = p4.x, y4 = p4.y;

    const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
    if (Math.abs(denom) < 1e-9) return null;

    const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
    return { x: x1 + ua * (x2 - x1), y: y1 + ua * (y2 - y1) };
}

// Get offset lines for a wall segment
function getWallLines(start: Vec2, end: Vec2, thickness: number) {
    const dir = normalize(sub(end, start));
    const perp = { x: -dir.y, y: dir.x };
    const halfThick = thickness / 2;

    const offset = mul(perp, halfThick);

    return {
        left: {
            p1: add(start, offset),
            p2: add(end, offset)
        },
        right: {
            p1: sub(start, offset),
            p2: sub(end, offset)
        }
    };
}

// ---------------------------------------------------------------------------
// Footprint sanitization — a wall footprint quad [startL, endL, endR, startR]
// whose long edges cross (a "bowtie") renders as two joined triangles, the
// butterfly-wing shape users report. The join math below never emits one, but
// profiles also arrive from stored documents and agent-authored payloads, so
// every consumer passes its quad through this guard: a healthy quad passes
// through untouched, a crossed one is rebuilt from its own corners.
// ---------------------------------------------------------------------------

/** True when open segments a→b and c→d properly cross between their endpoints. */
function segmentsProperlyCross(a: Vec2, b: Vec2, c: Vec2, d: Vec2): boolean {
    const d1 = (b.x - a.x) * (d.y - c.y) - (b.y - a.y) * (d.x - c.x);
    if (Math.abs(d1) < 1e-12) return false;
    const t = ((c.x - a.x) * (d.y - c.y) - (c.y - a.y) * (d.x - c.x)) / d1;
    const u = ((c.x - a.x) * (b.y - a.y) - (c.y - a.y) * (b.x - a.x)) / d1;
    return t > 1e-9 && t < 1 - 1e-9 && u > 1e-9 && u < 1 - 1e-9;
}

/** A 4-point footprint is a bowtie when either pair of opposite edges crosses. */
export function isBowtieProfile(profile: number[][] | undefined | null): boolean {
    if (!Array.isArray(profile) || profile.length !== 4) return false;
    const [a, b, c, d] = profile;
    if (!a || !b || !c || !d) return false;
    const v = (p: number[]): Vec2 => ({ x: p[0], y: p[1] });
    return segmentsProperlyCross(v(a), v(b), v(c), v(d)) ||
        segmentsProperlyCross(v(b), v(c), v(d), v(a));
}

/**
 * Repairs a crossed 4-point wall footprint by re-deriving the corner order
 * from the wall's own location line: every corner is classified onto the left
 * or right face (sign of the cross product with the run direction) and the
 * quad is reassembled [startL, endL, endR, startR] by run-line projection.
 * Miter positions survive — they only slide corners along their face line, so
 * each face keeps both its corners. Returns the original array when healthy,
 * the repaired quad when crossed, and null when the corners cannot form a
 * wall footprint (wrong count, degenerate faces).
 */
export function sanitizeWallProfileQuad(
    profile: number[][] | undefined | null,
    start: number[] | undefined,
    end: number[] | undefined
): number[][] | null {
    if (!Array.isArray(profile) || profile.length !== 4) return null;
    if (!isBowtieProfile(profile)) return profile;
    if (!Array.isArray(start) || !Array.isArray(end) || start.length < 2 || end.length < 2) return null;
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) return null;
    const ux = dx / len;
    const uy = dy / len;

    interface Corner { along: number; side: number }
    const lefts: Corner[] = [];
    const rights: Corner[] = [];
    for (const p of profile as number[][]) {
        if (!Array.isArray(p) || p.length < 2 || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) return null;
        const px = p[0] - start[0];
        const py = p[1] - start[1];
        const side = ux * py - uy * px;
        const along = ux * px + uy * py;
        (side >= 0 ? lefts : rights).push({ along, side });
    }
    if (lefts.length !== 2 || rights.length !== 2) return null;
    lefts.sort((m, n) => m.along - n.along);
    rights.sort((m, n) => m.along - n.along);
    const [lStart, lEnd] = lefts;
    const [rStart, rEnd] = rights;
    if (lEnd.along - lStart.along < 1e-9 || rEnd.along - rStart.along < 1e-9) return null;

    // Rebuild from the location line: p = start + dir*along + normal*side.
    const nx = -uy;
    const ny = ux;
    const point = (along: number, side: number): number[] => [
        start[0] + ux * along + nx * side,
        start[1] + uy * along + ny * side,
    ];
    return [
        point(lStart.along, lStart.side),
        point(lEnd.along, lEnd.side),
        point(rEnd.along, rEnd.side),
        point(rStart.along, rStart.side),
    ];
}

/**
 * Solves geometry for a single joint (node) where multiple walls meet.
 * Implements the standard "Sort by Angle + Bisector" algorithm.
 */
function solveJoint(
    joint: Vec2,
    walls: { id: string, data: WallData, isStart: boolean }[],
    plane: 'XY' | 'XZ'
): Record<string, { left: Vec2, right: Vec2 }> {
    const updates: Record<string, { left: Vec2, right: Vec2 }> = {};

    if (walls.length < 2) return updates;

    // 1. Sort walls by angle around the joint
    const sortedWalls = walls.map(w => {
        const start = w.isStart ? toVec2(new THREE.Vector3(...w.data.start), plane) : toVec2(new THREE.Vector3(...w.data.end), plane);
        const end = w.isStart ? toVec2(new THREE.Vector3(...w.data.end), plane) : toVec2(new THREE.Vector3(...w.data.start), plane);
        // Vector pointing OUT from the joint
        const dir = normalize(sub(end, start));
        const angle = Math.atan2(dir.y, dir.x);
        return { ...w, dir, angle };
    }).sort((a, b) => a.angle - b.angle);

    // 2. Iterate through adjacent pairs and find miter lines
    for (let i = 0; i < sortedWalls.length; i++) {
        const w1 = sortedWalls[i];
        const w2 = sortedWalls[(i + 1) % sortedWalls.length];

        const w1Lines = getWallLines(joint, add(joint, mul(w1.dir, 10)), w1.data.thickness);
        const w2Lines = getWallLines(joint, add(joint, mul(w2.dir, 10)), w2.data.thickness);

        // Intersect w1.Left with w2.Right
        const intersection = getLineIntersection(
            w1Lines.left.p1, w1Lines.left.p2,
            w2Lines.right.p1, w2Lines.right.p2
        );

        if (intersection) {
            // Assign to w1 Left corner
            if (!updates[w1.id]) updates[w1.id] = { left: intersection, right: intersection }; // Init
            updates[w1.id].left = intersection;

            // Assign to w2 Right corner
            if (!updates[w2.id]) updates[w2.id] = { left: intersection, right: intersection };
            updates[w2.id].right = intersection;
        }
    }

    return updates;
}

/**
 * Main Graph-Based Join Calculation
 */
function calculateGraphJoins(

    allWalls: { id: string, data: WallData }[],
    plane: 'XY' | 'XZ'
): Record<string, { profile: number[][], joinedAtStart: boolean, joinedAtEnd: boolean }> {
    const updates: Record<string, { profile: number[][], joinedAtStart: boolean, joinedAtEnd: boolean }> = {};
    const tolerance = 0.1;

    // 1. Identify Nodes (Joints)
    // Map of "x,y" string to list of walls ending there
    const joints: Record<string, { id: string, data: WallData, isStart: boolean }[]> = {};
    const jointPoints: Record<string, Vec2> = {};

    allWalls.forEach(w => {
        const start = toVec2(new THREE.Vector3(...w.data.start), plane);
        const end = toVec2(new THREE.Vector3(...w.data.end), plane);

        // Snap to existing joints if possible (simple grid snap for key)
        // Better: iterate existing keys and check distance
        let startKey = `${start.x.toFixed(3)},${start.y.toFixed(3)}`;
        let endKey = `${end.x.toFixed(3)},${end.y.toFixed(3)}`;

        // Check proximity to existing joints to merge
        for (const key in jointPoints) {
            if (dist(start, jointPoints[key]) < tolerance) startKey = key;
            if (dist(end, jointPoints[key]) < tolerance) endKey = key;
        }

        if (!joints[startKey]) { joints[startKey] = []; jointPoints[startKey] = start; }
        if (!joints[endKey]) { joints[endKey] = []; jointPoints[endKey] = end; }

        joints[startKey].push({ id: w.id, data: w.data, isStart: true });
        joints[endKey].push({ id: w.id, data: w.data, isStart: false });
    });

    // 2. Solve each Joint
    const wallCornerUpdates: Record<string, { startLeft?: Vec2, startRight?: Vec2, endLeft?: Vec2, endRight?: Vec2, joinedStart?: boolean, joinedEnd?: boolean }> = {};

    Object.keys(joints).forEach(key => {
        const wallsAtJoint = joints[key];
        const jointPt = jointPoints[key];

        // Only process if > 1 wall (otherwise it's an open end)
        if (wallsAtJoint.length > 1) {
            const jointUpdates = solveJoint(jointPt, wallsAtJoint, plane);

            // Merge into global updates
            Object.entries(jointUpdates).forEach(([id, corners]) => {
                if (!wallCornerUpdates[id]) wallCornerUpdates[id] = {};

                // Find which end of the wall this is
                const w = wallsAtJoint.find(w => w.id === id);
                if (w) {
                    if (w.isStart) {
                        wallCornerUpdates[id].startLeft = corners.left;
                        wallCornerUpdates[id].startRight = corners.right;
                        wallCornerUpdates[id].joinedStart = true;
                    } else {
                        wallCornerUpdates[id].endRight = corners.left;
                        wallCornerUpdates[id].endLeft = corners.right;
                        wallCornerUpdates[id].joinedEnd = true;
                    }
                }
            });
        }
    });

    // 3. Construct Final Profiles
    allWalls.forEach(w => {
        const corners = wallCornerUpdates[w.id];
        if (corners) {
            const start = toVec2(new THREE.Vector3(...w.data.start), plane);
            const end = toVec2(new THREE.Vector3(...w.data.end), plane);
            const lines = getWallLines(start, end, w.data.thickness);

            // Default corners
            let pStartLeft = lines.left.p1;
            let pEndLeft = lines.left.p2;
            let pEndRight = lines.right.p2;
            let pStartRight = lines.right.p1;

            // Override with calculated joins
            if (corners.startLeft) pStartLeft = corners.startLeft;
            if (corners.startRight) pStartRight = corners.startRight;
            if (corners.endLeft) pEndLeft = corners.endLeft;
            if (corners.endRight) pEndRight = corners.endRight;

            const assembled = [
                [pStartLeft.x, pStartLeft.y],
                [pEndLeft.x, pEndLeft.y],
                [pEndRight.x, pEndRight.y],
                [pStartRight.x, pStartRight.y]
            ];
            updates[w.id] = {
                // A crossed footprint never reaches the document: the sanitizer
                // passes healthy quads through and rebuilds bowties from the
                // wall's own location line.
                profile: sanitizeWallProfileQuad(assembled, w.data.start, w.data.end) ?? assembled,
                joinedAtStart: !!corners.joinedStart,
                joinedAtEnd: !!corners.joinedEnd
            };
        }
    });

    return updates;
}

/**
 * Update wall geometries to handle joins (BIM workbench)
 */
export function updateWallJoins(
    entities: Record<string, Entity>
): Record<string, Partial<Entity>> {
    const updates: Record<string, Partial<Entity>> = {};
    const others: { id: string, data: WallData }[] = [];

    Object.values(entities).forEach(e => {
        if (e.type === 'wall' && hasUsableWallData(e.data)) {
            others.push({ id: e.id, data: e.data as WallData });
        }
    });

    // Z-up world: wall footprints live in the XY ground plane now.
    const profileUpdates = calculateGraphJoins(others, 'XY');

    Object.entries(profileUpdates).forEach(([id, update]) => {
        updates[id] = {
            data: {
                ...entities[id].data,
                profile: update.profile,
                joinedAtStart: update.joinedAtStart,
                joinedAtEnd: update.joinedAtEnd
            }
        };
    });

    return updates;
}

/**
 * Update wall geometries to handle joins (BIM Mode)
 */
export function updateBIMWallJoins(
    elements: Map<string, BIMElement>
): Map<string, Partial<WallElement>> {
    const updates = new Map<string, Partial<WallElement>>();
    const others: { id: string, data: WallData }[] = [];

    elements.forEach(e => {
        if (e.category === 'Walls') {
            const w = e as WallElement;
            if (!hasUsableWallData(w.geometryData)) return;
            others.push({
                id: e.id,
                data: {
                    start: w.geometryData.start,
                    end: w.geometryData.end,
                    thickness: w.geometryData.thickness,
                    height: w.geometryData.height,
                    profile: w.geometryData.profile
                }
            });
        }
    });

    const profileUpdates = calculateGraphJoins(others, 'XY');

    Object.entries(profileUpdates).forEach(([id, update]) => {
        const element = elements.get(id);
        if (element && element.category === 'Walls') {
            const w = element as WallElement;
            updates.set(id, {
                geometryData: {
                    ...w.geometryData,
                    profile: update.profile as any,
                    joinedAtStart: update.joinedAtStart,
                    joinedAtEnd: update.joinedAtEnd
                }
            });
        }
    });

    return updates;
}

// ===========================================
// BIM-style Joint-Aware Wall Move
// ===========================================

/** Endpoint coincidence tolerance (meters) — mirrors WallJoinSolver joins. */
const WALL_MOVE_TOLERANCE = 0.01;

/** Endpoint pair used by the joint-aware geometry re-solve pipeline. */
export type WallEndpointGeometry = {
    start: [number, number, number];
    end: [number, number, number];
};
/** True when any endpoint pair of the two walls coincides within tolerance. */
function wallsShareEndpoint(
    a: Pick<WallElement, 'geometryData'>,
    b: Pick<WallElement, 'geometryData'>,
    tolerance: number = WALL_MOVE_TOLERANCE
): boolean {
    const aStart = new THREE.Vector3(...a.geometryData.start);
    const aEnd = new THREE.Vector3(...a.geometryData.end);
    const bStart = new THREE.Vector3(...b.geometryData.start);
    const bEnd = new THREE.Vector3(...b.geometryData.end);
    return aStart.distanceTo(bStart) < tolerance || aStart.distanceTo(bEnd) < tolerance ||
        aEnd.distanceTo(bStart) < tolerance || aEnd.distanceTo(bEnd) < tolerance;
}

/**
 * True when `point` lies on the body of the wall segment (T-junction stem
 * test) — mirrors WallJoinSolver.applyJoinsAtEnd's detection so recomputed
 * profiles keep seeing the same joins the renderer solves.
 */
function pointOnWallBody(point: THREE.Vector3, wall: Pick<WallElement, 'geometryData'>): boolean {
    const start = new THREE.Vector3(...wall.geometryData.start);
    const end = new THREE.Vector3(...wall.geometryData.end);
    const segment = end.clone().sub(start);
    const length = segment.length();
    if (length < 0.001) return false;

    const dir = segment.normalize();
    const toPoint = point.clone().sub(start);
    const along = toPoint.dot(dir);
    const projected = start.clone().add(dir.multiplyScalar(along));
    return projected.distanceTo(point) < WALL_MOVE_TOLERANCE &&
        along > WALL_MOVE_TOLERANCE && along < length - WALL_MOVE_TOLERANCE;
}

/**
 * Recompute the render geometry (layerProfiles + profile) of a wall against
 * its post-move neighbors. The renderer draws walls from the layer profile
 * polygons, so a move that only rewrites start/end would leave the stale
 * polygon behind and the wall would visually stay in place.
 */
export function solveWallRenderGeometry(
    wall: WallElement,
    geometry: WallEndpointGeometry,
    allWalls: WallElement[]
): Partial<WallElement>['geometryData'] {
    const postMove: WallElement = { ...wall, geometryData: { ...wall.geometryData, ...geometry } };

    const candidates = allWalls.filter(other =>
        other.id !== wall.id &&
        (wallsShareEndpoint(postMove, other) ||
            pointOnWallBody(new THREE.Vector3(...postMove.geometryData.start), other) ||
            pointOnWallBody(new THREE.Vector3(...postMove.geometryData.end), other))
    );

    const layerProfiles = WallJoinSolver.computeLayerProfiles(postMove, candidates);

    return {
        ...wall.geometryData,
        ...geometry,
        layerProfiles,
        profile: layerProfiles.length > 0 ? layerProfiles[0] : undefined,
        joinedAtStart: candidates.some(c =>
            new THREE.Vector3(...c.geometryData.start).distanceTo(new THREE.Vector3(...postMove.geometryData.start)) < WALL_MOVE_TOLERANCE ||
            new THREE.Vector3(...c.geometryData.end).distanceTo(new THREE.Vector3(...postMove.geometryData.start)) < WALL_MOVE_TOLERANCE),
        joinedAtEnd: candidates.some(c =>
            new THREE.Vector3(...c.geometryData.start).distanceTo(new THREE.Vector3(...postMove.geometryData.end)) < WALL_MOVE_TOLERANCE ||
            new THREE.Vector3(...c.geometryData.end).distanceTo(new THREE.Vector3(...postMove.geometryData.end)) < WALL_MOVE_TOLERANCE)
    };
}

/**
 * Build the update batch for a BIM-style wall move.
 *
 * - Each moved wall translates wholesale (both endpoints).
 * - Any connected wall sharing a traveling joint extends along its own
 *   alignment: only the shared endpoint follows the move, the far endpoint
 *   stays, so the wall stretches/shrinks in place (BIM behavior).
 * - Walls with no connection to the moved set simply translate; walls with
 *   no connection at all are untouched.
 * - Every affected wall gets its render geometry re-solved, fixing the
 *   "wall does not move visually" bug caused by stale layer profiles.
 *
 * `stretchConnected: false` (parallel-offset moves) keeps connected walls in
 * place instead of stretching them, but still re-solves their render
 * geometry so miters at the vacated joints revert to clean ends.
 */
export function computeWallMoveUpdates(
    elements: Map<string, BIMElement>,
    movedIds: string[],
    translation: THREE.Vector3,
    options: { stretchConnected?: boolean } = {}
): Map<string, Partial<WallElement>> {
    const stretchConnected = options.stretchConnected !== false;
    const updates = new Map<string, Partial<WallElement>>();
    if (translation.lengthSq() < 1e-12) return updates;

    const allWalls: WallElement[] = [];
    elements.forEach(el => {
        if (el.category === 'Walls' && hasUsableWallData((el as WallElement).geometryData)) {
            allWalls.push(el as WallElement);
        }
    });

    const movedSet = new Set(movedIds);
    const movedJoints: THREE.Vector3[] = [];
    const newEndpoints = new Map<string, WallEndpointGeometry>();

    for (const id of movedIds) {
        const wall = allWalls.find(w => w.id === id);
        if (!wall) continue;
        const start = new THREE.Vector3(...wall.geometryData.start);
        const end = new THREE.Vector3(...wall.geometryData.end);
        movedJoints.push(start, end);
        newEndpoints.set(id, {
            start: [start.x + translation.x, start.y + translation.y, start.z + translation.z],
            end: [end.x + translation.x, end.y + translation.y, end.z + translation.z]
        });
    }

    if (movedJoints.length === 0) return updates;

    // Connected walls stretch: only the endpoint sitting on a traveling
    // joint follows the move, keeping the wall's own alignment.
    for (const wall of allWalls) {
        if (movedSet.has(wall.id)) continue;
        const start = new THREE.Vector3(...wall.geometryData.start);
        const end = new THREE.Vector3(...wall.geometryData.end);
        const startMoves = movedJoints.some(j => start.distanceTo(j) < WALL_MOVE_TOLERANCE);
        const endMoves = movedJoints.some(j => end.distanceTo(j) < WALL_MOVE_TOLERANCE);
        if (!startMoves && !endMoves) continue;

        if (stretchConnected) {
            newEndpoints.set(wall.id, {
                start: startMoves
                    ? [start.x + translation.x, start.y + translation.y, start.z + translation.z]
                    : [start.x, start.y, start.z],
                end: endMoves
                    ? [end.x + translation.x, end.y + translation.y, end.z + translation.z]
                    : [end.x, end.y, end.z]
            });
        } else {
            // Disjoin mode: the neighbor keeps its geometry, but it must be
            // re-solved so the miter at the vacated joint becomes a clean end.
            if (!newEndpoints.has(wall.id)) {
                newEndpoints.set(wall.id, {
                    start: [start.x, start.y, start.z],
                    end: [end.x, end.y, end.z]
                });
            }
        }
    }

    // Re-solve render geometry for every affected wall against the post-move
    // world state so joined corners stay mitered.
    for (const [id, geometry] of newEndpoints) {
        const wall = allWalls.find(w => w.id === id);
        if (!wall) continue;
        updates.set(id, { geometryData: solveWallRenderGeometry(wall, geometry, allWalls) });
    }

    return updates;
}

/**
 * Create a translated copy of a wall for the Move tool's copy mode. Copies
 * leave their joins behind (BIM: copies are unjoined until re-joined), so
 * the clone gets a straight render geometry computed from its own shape.
 */
export function createTranslatedWallCopy(
    wall: WallElement,
    translation: THREE.Vector3
): WallElement {
    const start = new THREE.Vector3(...wall.geometryData.start).add(translation);
    const end = new THREE.Vector3(...wall.geometryData.end).add(translation);
    const geometry: WallEndpointGeometry = {
        start: [start.x, start.y, start.z],
        end: [end.x, end.y, end.z]
    };

    const copy: WallElement = {
        ...wall,
        id: THREE.MathUtils.generateUUID(),
        geometryData: {
            ...wall.geometryData,
            ...geometry,
            joinedAtStart: false,
            joinedAtEnd: false
        },
        constraints: []
    };

    const layerProfiles = WallJoinSolver.computeLayerProfiles(copy, []);
    copy.geometryData = {
        ...copy.geometryData,
        layerProfiles,
        profile: layerProfiles.length > 0 ? layerProfiles[0] : undefined
    };

    return copy;
}
